import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClassesContext from '../../context/ClassesContext';
import CasesContext from '../../context/CasesContext';
import AacnContext from '../../context/AacnContext';
import { createClass, renameClass, deleteClass, addStudentToClass, removeStudentFromClass, addCaseToClass, removeCaseFromClass, reorderClassCases, fetchClassCompletion, fetchClassIndicatorTrends } from '../../utils/classApi';
import { SEMESTER_TAG_OPTIONS, CLASS_YEAR_OPTIONS, combineSemesterYear, splitSemesterYear } from './Classes';
import FilterSelect from './FilterSelect';
import ClassGradingProgress from './ClassGradingProgress';
import './ClassDetail.css';
import Footer from '../Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';

function AssignPanel({ items, emptyLabel, onPick }) {
  if (items.length === 0) {
    return <p className="chat-card-hint assign-panel-empty">{emptyLabel}</p>;
  }
  return (
    <div className="assign-panel">
      {items.map((item) => (
        <button key={item.id} className="assign-panel-row" onClick={() => onPick(item.id)}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

// Same square-tile look as the Simulation Cases page (reuses its .cases-list/.case-card
// styling, which is globally bundled) rather than the plain list rows AssignPanel uses.
// Shows every case at once — assigned ones highlighted in place — so toggling a case
// on/off never makes it jump between a "pick" grid and a separate "assigned" list.
function AssignCaseTiles({ items, emptyLabel, assignedIds, onToggle }) {
  if (items.length === 0) {
    return <p className="chat-card-hint assign-panel-empty">{emptyLabel}</p>;
  }
  return (
    <div className="cases-list assign-case-tiles">
      {items.map((item) => {
        const isAssigned = assignedIds.has(item.id);
        return (
          <div
            key={item.id}
            className={`case-card ${isAssigned ? 'case-card-selected' : ''}`}
            onClick={() => onToggle(item.id, isAssigned)}
          >
            <div className="case-card-main">
              <span className="case-name">{item.label}</span>
            </div>
            {isAssigned && <span className="case-card-selected-check">✓</span>}
          </div>
        );
      })}
    </div>
  );
}

const PROGRESSION_CHART_WIDTH = 700;
const PROGRESSION_CHART_HEIGHT = 280;
const PROGRESSION_PAD_LEFT = 48;
const PROGRESSION_PAD_RIGHT = 20;
const PROGRESSION_PAD_TOP = 16;
const PROGRESSION_PAD_BOTTOM = 36;
const PROGRESSION_PLOT_WIDTH = PROGRESSION_CHART_WIDTH - PROGRESSION_PAD_LEFT - PROGRESSION_PAD_RIGHT;
const PROGRESSION_PLOT_HEIGHT = PROGRESSION_CHART_HEIGHT - PROGRESSION_PAD_TOP - PROGRESSION_PAD_BOTTOM;

// Converts one backend timeline entry's raw counts into 0-100 percentages
// for charting. A period with nothing graded yet for this code renders as
// zero across the board rather than dividing by zero.
function levelPercentages(counts) {
  const total = (counts?.developed || 0) + (counts?.developing || 0) + (counts?.none || 0);
  if (!total) return { developed: 0, developing: 0, none: 0 };
  return {
    developed: Math.round((counts.developed / total) * 100),
    developing: Math.round((counts.developing / total) * 100),
    none: Math.round((counts.none / total) * 100),
  };
}

function formatPeriodLabel(periodStart) {
  const d = new Date(periodStart);
  return isNaN(d) ? periodStart : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Same three-level colors used everywhere else in the app for AACN grading
// (indicator chips / breakdown panels on the Student Detail page).
const PROGRESSION_LEVELS = [
  { key: 'developed', label: 'Developed', className: 'progression-line-developed' },
  { key: 'developing', label: 'Developing', className: 'progression-line-developing' },
  { key: 'none', label: 'Not Yet Evidenced', className: 'progression-line-none' },
];

// The chart itself always renders — axes, gridlines, and the Developed/
// Developing/Not Yet Evidenced keys, regardless of how much (or how little)
// real data exists. With no sessions graded yet, no line is drawn at all
// (nothing is fabricated) — just the accurate, correctly-labeled empty frame
// plus an in-chart note. A single real student still plots normally,
// percentages are well-defined at n=1.
function ProgressionChart({ series, labels, emptyMessage = 'No sessions graded yet' }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const hasData = series.length > 0;
  const xStep = PROGRESSION_PLOT_WIDTH / (Math.max(series.length, 1) - 1 || 1);
  const xFor = (i) => PROGRESSION_PAD_LEFT + i * xStep;
  const yFor = (v) => PROGRESSION_PAD_TOP + PROGRESSION_PLOT_HEIGHT - (v / 100) * PROGRESSION_PLOT_HEIGHT;
  const pathFor = (levelKey) => series
    .map((point, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(point[levelKey])}`)
    .join(' ');
  const hovered = hoverIndex !== null ? series[hoverIndex] : null;

  return (
    <div className="progression-chart-wrap">
      <svg width={PROGRESSION_CHART_WIDTH} height={PROGRESSION_CHART_HEIGHT} className="progression-chart-svg">
        <text
          x={16}
          y={PROGRESSION_PAD_TOP + PROGRESSION_PLOT_HEIGHT / 2}
          textAnchor="middle"
          className="progression-axis-title"
          transform={`rotate(-90, 16, ${PROGRESSION_PAD_TOP + PROGRESSION_PLOT_HEIGHT / 2})`}
        >
          % of students
        </text>

        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line
              x1={PROGRESSION_PAD_LEFT} y1={yFor(v)} x2={PROGRESSION_CHART_WIDTH - PROGRESSION_PAD_RIGHT} y2={yFor(v)}
              className="progression-gridline"
            />
            <text x={PROGRESSION_PAD_LEFT - 8} y={yFor(v) + 3} className="progression-axis-label" textAnchor="end">
              {v}%
            </text>
          </g>
        ))}

        {hasData && labels.map((label, i) => (
          <text
            key={i}
            x={xFor(i)}
            y={PROGRESSION_CHART_HEIGHT - PROGRESSION_PAD_BOTTOM + 20}
            textAnchor="middle"
            className="progression-axis-label"
          >
            {label}
          </text>
        ))}

        {hasData && PROGRESSION_LEVELS.map(({ key, className }) => (
          <g key={key}>
            <path d={pathFor(key)} className={`progression-line ${className}`} fill="none" />
            {series.map((point, i) => (
              <circle
                key={i}
                cx={xFor(i)}
                cy={yFor(point[key])}
                r={hoverIndex === i ? 4.5 : 2.5}
                className={`progression-dot ${className}`}
              />
            ))}
          </g>
        ))}

        {hasData && hoverIndex !== null && (
          <line
            x1={xFor(hoverIndex)} y1={PROGRESSION_PAD_TOP} x2={xFor(hoverIndex)} y2={PROGRESSION_CHART_HEIGHT - PROGRESSION_PAD_BOTTOM}
            className="progression-hover-line"
          />
        )}

        {hasData && series.map((_, i) => (
          <rect
            key={i}
            x={xFor(i) - xStep / 2}
            y={PROGRESSION_PAD_TOP}
            width={xStep || PROGRESSION_PLOT_WIDTH}
            height={PROGRESSION_PLOT_HEIGHT}
            fill="transparent"
            onMouseEnter={() => setHoverIndex(i)}
            onMouseLeave={() => setHoverIndex(null)}
          />
        ))}

        {!hasData && (
          <text
            x={PROGRESSION_PAD_LEFT + PROGRESSION_PLOT_WIDTH / 2}
            y={PROGRESSION_PAD_TOP + PROGRESSION_PLOT_HEIGHT / 2}
            textAnchor="middle"
            className="progression-empty-svg-text"
          >
            {emptyMessage}
          </text>
        )}
      </svg>

      {hovered && (
        <div className="progression-tooltip" style={{ left: xFor(hoverIndex) }}>
          <div className="progression-tooltip-label">{labels[hoverIndex]}</div>
          {PROGRESSION_LEVELS.map(({ key, label, className }) => (
            <div key={key} className="progression-tooltip-row">
              <span className={`progression-legend-swatch ${className}`} />
              {label}: {hovered[key]}%
            </div>
          ))}
        </div>
      )}

      <div className="progression-legend">
        {PROGRESSION_LEVELS.map(({ key, label, className }) => (
          <span key={key} className="progression-legend-item">
            <span className={`progression-legend-swatch ${className}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ClassDetail({ isTeacher }) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { classes, setClasses, loading: classesLoading, fetchClasses } = useContext(ClassesContext);
  const { cases } = useContext(CasesContext);
  const { getSubCompetencyByCode, loading: aacnLoading } = useContext(AacnContext);
  const [progressionOpen, setProgressionOpen] = useState(false);
  const [trends, setTrends] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [trendsError, setTrendsError] = useState(false);
  const [trendsGroup, setTrendsGroup] = useState('student');
  // Bumped when ClassGradingProgress finishes grading sessions, so the
  // grade-dependent data below (indicator trends) is refetched in place.
  const [gradesVersion, setGradesVersion] = useState(0);

  const [allStudents, setAllStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [updatingSemester, setUpdatingSemester] = useState(false);

  const [assigningCase, setAssigningCase] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [addStudentSearchQuery, setAddStudentSearchQuery] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  // '' | 'notDone' | 'done' (every assigned case) | 'notDone:<caseId>' | 'done:<caseId>'
  const [completionFilter, setCompletionFilter] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const [completion, setCompletion] = useState(null);
  const [loadingCompletion, setLoadingCompletion] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/students`)
      .then((res) => { if (Array.isArray(res.data)) setAllStudents(res.data); })
      .catch(() => setAllStudents([]))
      .finally(() => setStudentsLoading(false));
  }, []);

  useEffect(() => {
    setLoadingCompletion(true);
    fetchClassCompletion(classId)
      .then((res) => setCompletion(res.data))
      .catch(() => setCompletion(null))
      .finally(() => setLoadingCompletion(false));
  }, [classId]);

  useEffect(() => {
    // Ignore responses from a previous class/group if the picker is switched
    // before the earlier request settles.
    let cancelled = false;
    setTrendsLoading(true);
    setTrendsError(false);
    fetchClassIndicatorTrends(classId, trendsGroup)
      .then((res) => { if (!cancelled) setTrends(res.data); })
      .catch(() => { if (!cancelled) setTrendsError(true); })
      .finally(() => { if (!cancelled) setTrendsLoading(false); });
    return () => { cancelled = true; };
  }, [classId, trendsGroup, gradesVersion]);

  const classItem = classes.find((c) => c.id === classId);

  const updateClassLocal = (updater) => {
    setClasses((prev) => prev.map((c) => c.id === classId ? updater(c) : c));
  };

  const startRename = () => {
    setNameDraft(classItem.name);
    setEditingName(true);
  };

  const commitRename = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === classItem.name) { setEditingName(false); return; }
    setRenaming(true);
    try {
      // renameClass updates both name and tag together — pass the existing tag through
      // unchanged so renaming the class doesn't wipe whatever tag was already set.
      await renameClass(classId, trimmed, classItem.tag);
      updateClassLocal((c) => ({ ...c, name: trimmed }));
    } catch {
      alert('Could not rename the class. Please try again.');
    } finally {
      setRenaming(false);
      setEditingName(false);
    }
  };

  const handleSemesterYearChange = async (newSemester, newYear) => {
    const newTag = combineSemesterYear(newSemester, newYear);
    setUpdatingSemester(true);
    try {
      await renameClass(classId, classItem.name, newTag);
      updateClassLocal((c) => ({ ...c, tag: newTag || null }));
    } catch {
      alert('Could not update the semester/year. Please try again.');
    } finally {
      setUpdatingSemester(false);
    }
  };

  const handleAssignCase = async (caseId) => {
    // Optimistic: highlight the tile immediately instead of waiting on the round-trip,
    // then reconcile with the backend's de-duplicated object once it responds. Roll back
    // by id (not a snapshot) so it's safe even if other toggles happened in between.
    const caseInfo = cases.find((c) => c.id === caseId);
    updateClassLocal((c) => ({
      ...c,
      cases: [...(c.cases || []), { id: caseId, name: caseInfo?.name || '', description: '' }],
    }));
    try {
      const res = await addCaseToClass(classId, caseId);
      updateClassLocal(() => res.data);
    } catch (err) {
      updateClassLocal((c) => ({ ...c, cases: (c.cases || []).filter((item) => item.id !== caseId) }));
      alert(err.response?.data?.error || 'Could not assign that case. Please try again.');
    }
  };

  const handleRemoveCase = async (caseId) => {
    const removedItem = (classItem.cases || []).find((c) => c.id === caseId);
    updateClassLocal((c) => ({ ...c, cases: (c.cases || []).filter((item) => item.id !== caseId) }));
    try {
      await removeCaseFromClass(classId, caseId);
    } catch {
      if (removedItem) {
        updateClassLocal((c) => ({ ...c, cases: [...(c.cases || []), removedItem] }));
      }
      alert('Could not remove that case. Please try again.');
    }
  };

  const handleToggleCase = (caseId, isAssigned) => {
    if (isAssigned) {
      handleRemoveCase(caseId);
    } else {
      handleAssignCase(caseId);
    }
  };

  const dragCaseItem = useRef(null);
  const dragOverCaseItem = useRef(null);

  const handleCaseDragStart = (caseId) => { dragCaseItem.current = caseId; };
  const handleCaseDragOver = (caseId) => { dragOverCaseItem.current = caseId; };
  const handleCaseDrop = () => {
    const fromId = dragCaseItem.current;
    const toId = dragOverCaseItem.current;
    dragCaseItem.current = null;
    dragOverCaseItem.current = null;
    if (!fromId || fromId === toId) return;

    const current = classItem.cases || [];
    const fromIdx = current.findIndex((c) => c.id === fromId);
    const toIdx = current.findIndex((c) => c.id === toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = [...current];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Update instantly — no network round-trip before the drag visually
    // completes. Only fall back to re-fetching the authoritative case list
    // if the save actually fails (e.g. local state had drifted from the
    // backend), instead of checking on every single drag.
    const previous = current;
    updateClassLocal((c) => ({ ...c, cases: reordered }));
    reorderClassCases(classId, reordered.map((c) => c.id)).catch(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/classes/${classId}`);
        updateClassLocal((c) => ({ ...c, cases: res.data?.cases || previous }));
      } catch {
        updateClassLocal((c) => ({ ...c, cases: previous }));
      }
      alert('Could not save the new case order — the list may have changed. Please try again.');
    });
  };

  const handleAddStudent = async (studentId) => {
    try {
      const res = await addStudentToClass(classId, studentId);
      // Same rationale as handleAssignCase — trust the backend's returned
      // class object instead of appending a locally-built entry.
      updateClassLocal(() => res.data);
    } catch {
      alert('Could not add that student. Please try again.');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await removeStudentFromClass(classId, studentId);
      updateClassLocal((c) => ({ ...c, students: (c.students || []).filter((item) => item.id !== studentId) }));
    } catch {
      alert('Could not remove that student. Please try again.');
    }
  };

  const handleDuplicateClass = async () => {
    setDuplicating(true);
    try {
      const res = await createClass(`${classItem.name} (Copy)`, classItem.tag);
      const newClassId = res.data.id;
      await Promise.all([
        ...(classItem.cases || []).map((c) => addCaseToClass(newClassId, c.id)),
        ...(classItem.students || []).map((s) => addStudentToClass(newClassId, s.id)),
      ]);
      await fetchClasses();
      navigate(`/admin/classes/${newClassId}`);
    } catch {
      alert('Could not duplicate this class. Please try again.');
      setDuplicating(false);
    }
  };

  const handleDeleteClass = async () => {
    setDeleting(true);
    try {
      await deleteClass(classId);
      setClasses((prev) => prev.filter((c) => c.id !== classId));
      navigate('/admin/classes');
    } catch {
      alert('Could not delete the class. Please try again.');
      setDeleting(false);
    }
  };

  if (classesLoading || studentsLoading) {
    return (
      <div className="root-container">
        <div className="component-container">
          <p style={{ padding: '50px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!classItem) {
    return (
      <div className="root-container">
        <div className="component-container">
          <p style={{ padding: '50px' }}>Class not found.</p>
        </div>
      </div>
    );
  }

  const { semester: currentSemester, year: currentYear } = splitSemesterYear(classItem.tag);

  const assignedCases = classItem.cases || [];
  const assignedStudents = classItem.students || [];
  const assignedCaseIds = new Set(assignedCases.map((c) => c.id));
  const assignedStudentIds = new Set(assignedStudents.map((s) => s.id));
  const availableStudents = allStudents.filter((s) => !assignedStudentIds.has(s.id));
  const normalizedAddStudentQuery = addStudentSearchQuery.trim().toLowerCase();
  const filteredAvailableStudents = availableStudents.filter((s) =>
    !normalizedAddStudentQuery || s.name.toLowerCase().includes(normalizedAddStudentQuery)
  );
  // classItem.students only has {id, name} — cross-reference against the full
  // roster (which has `tag`) to split the class's roster into real vs demo.
  const tagByStudentId = new Map(allStudents.map((s) => [s.id, s.tag]));
  const emailByStudentId = new Map(allStudents.map((s) => [s.id, s.email]));
  const allRealAssignedStudents = assignedStudents.filter((s) => tagByStudentId.get(s.id) !== 'demo');
  const allDemoAssignedStudents = assignedStudents.filter((s) => tagByStudentId.get(s.id) === 'demo');
  const realAssignedStudentCount = allRealAssignedStudents.length;
  const demoAssignedStudentCount = allDemoAssignedStudents.length;
  // Single source of truth for case numbering — defined once here from the
  // Simulation Cases list's own order, then reused by the completion table
  // below so "Case 1"/"Case 2" always means the same thing in both places.
  const caseNumberById = new Map(assignedCases.map((c, i) => [c.id, i + 1]));
  const progressionCodes = trends?.subCompetencies || [];
  const progressionLabels = (trends?.timeline || []).map((period) => formatPeriodLabel(period.periodStart));

  const completionByStudentId = new Map((completion?.students || []).map((s) => [s.id, s.completion]));

  // Students missing a session for any assigned case (a "—" cell) go to the top
  // so faculty see who still has work outstanding; relative order is otherwise
  // kept. Applied once completion data arrives. Prev/Next on Student Detail
  // follows this same order, since it's handed these lists.
  const hasIncompleteCase = (s) => assignedCases.some((c) => !(completionByStudentId.get(s.id)?.[c.id]?.sessionCount > 0));
  const incompleteFirst = (list) => (completion
    ? [...list.filter(hasIncompleteCase), ...list.filter((s) => !hasIncompleteCase(s))]
    : list);

  // Completion filter: "done" = at least one session, the same rule as the ✓.
  // With several cases, plain Complete/Incomplete mean all cases / any case, and
  // per-case options are added. Only applied once completion data is in.
  const isDoneForCase = (s, caseId) => completionByStudentId.get(s.id)?.[caseId]?.sessionCount > 0;
  const matchesCompletionFilter = (s) => {
    if (!completionFilter || !completion) return true;
    const [status, caseId] = completionFilter.split(':');
    const done = caseId ? isDoneForCase(s, caseId) : !hasIncompleteCase(s);
    return status === 'done' ? done : !done;
  };
  const completionFilterOptions = [
    { value: '', label: 'All' },
    { value: 'notDone', label: 'Incomplete' },
    { value: 'done', label: 'Complete' },
    ...(assignedCases.length > 1
      ? assignedCases.flatMap((c) => [
        { value: `notDone:${c.id}`, label: `Incomplete #${caseNumberById.get(c.id)}` },
        { value: `done:${c.id}`, label: `Complete #${caseNumberById.get(c.id)}` },
      ])
      : []),
  ];

  const normalizedStudentQuery = studentSearchQuery.trim().toLowerCase();
  const matchesStudentQuery = (s) => !normalizedStudentQuery || s.name.toLowerCase().includes(normalizedStudentQuery);
  const matchesStudentFilters = (s) => matchesStudentQuery(s) && matchesCompletionFilter(s);
  const filteredRealAssignedStudents = incompleteFirst(allRealAssignedStudents.filter(matchesStudentFilters));
  const filteredDemoAssignedStudents = incompleteFirst(allDemoAssignedStudents.filter(matchesStudentFilters));
  const isFilteringStudents = Boolean(normalizedStudentQuery || (completionFilter && completion));

  // Same behavior as the Students page's copy button: emails of the real
  // (non-demo) students currently shown, comma-separated for an email's To/BCC
  // — e.g. Completion "Incomplete" to remind students who haven't submitted.
  const visibleEmails = [...new Set(filteredRealAssignedStudents.map((s) => (emailByStudentId.get(s.id) || '').trim()).filter(Boolean))];
  const emailCountLabel = `${visibleEmails.length} email${visibleEmails.length === 1 ? '' : 's'}`;
  const handleCopyEmails = async () => {
    try {
      await navigator.clipboard.writeText(visibleEmails.join(', '));
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    setTimeout(() => setCopyStatus(''), 2500);
  };
  const showDemoStudentsSection = demoAssignedStudentCount > 0;

  // Hands StudentDetail the class context plus the exact list the row came
  // from (as currently filtered/ordered, real and demo kept separate) so it
  // can offer Prev/Next through this class's students and a way back here.
  const openStudent = (s, list) => navigate(`/admin/students/${s.id}`, {
    state: {
      fromClass: { id: classId, name: classItem.name },
      studentList: list.map(({ id, name }) => ({ id, name })),
      caseId: assignedCases[0]?.id || null,
    },
  });

  const renderCompletionRow = (s, list) => (
    <div key={s.id} className="completion-row student-row-clickable" onClick={() => openStudent(s, list)}>
      <span className="completion-col-name">
        <div className="student-avatar">{s.name.charAt(0)}</div>
        <span className="student-name">{s.name}</span>
      </span>
      {assignedCases.map((c) => {
        const entry = completionByStudentId.get(s.id)?.[c.id];
        const done = entry?.sessionCount > 0;
        return (
          <span
            key={c.id}
            className={`completion-col-case completion-cell ${done ? 'completion-cell-done' : 'completion-cell-pending'}`}
            title={`${c.name}: ${done ? `${entry.sessionCount} session${entry.sessionCount === 1 ? '' : 's'} — last on ${entry.lastSessionAt}` : 'No sessions yet'}`}
          >
            {done ? '✓' : '—'}
          </span>
        );
      })}
      <span className="completion-col-actions">
        <span className="student-view-link">View →</span>
        {!isTeacher && (
          <button
            className="class-remove-btn"
            onClick={(e) => { e.stopPropagation(); handleRemoveStudent(s.id); }}
            title="Remove from class"
          >×</button>
        )}
      </span>
    </div>
  );

  const completionHeaderRow = (
    <div className="completion-row completion-row-header">
      <span className="completion-col-name">Name</span>
      {assignedCases.map((c) => (
        <span key={c.id} className="completion-col-case" title={c.name}>#{caseNumberById.get(c.id)}</span>
      ))}
      <span className="completion-col-actions"></span>
    </div>
  );

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <button className="back-button" onClick={() => navigate('/admin/classes')}>
            ← Classes
          </button>
          <div className="title-name-container" style={{ marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {editingName ? (
              <input
                className="class-name-input class-name-input-header"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                disabled={renaming}
                autoFocus
              />
            ) : isTeacher ? (
              <header className="name-header">{classItem.name}</header>
            ) : (
              <header className="name-header name-header-editable" onClick={startRename} title="Click to rename">
                {classItem.name}
              </header>
            )}
            <span className="class-detail-semester-label">Semester</span>
            {isTeacher ? (
              <span className="class-tag-chip">{currentSemester || 'Not set'}</span>
            ) : (
              <select
                className="class-tag-select"
                aria-label="Semester"
                value={currentSemester}
                onChange={(e) => handleSemesterYearChange(e.target.value, currentYear)}
                disabled={updatingSemester}
              >
                {!currentSemester && <option value="" disabled>Select semester</option>}
                {SEMESTER_TAG_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            )}
            {isTeacher ? (
              <span className="class-tag-chip">{currentYear || 'Not set'}</span>
            ) : (
              <select
                className="class-tag-select"
                aria-label="Year"
                value={currentYear}
                onChange={(e) => handleSemesterYearChange(currentSemester, e.target.value)}
                disabled={updatingSemester}
              >
                {!currentYear && <option value="" disabled>Select year</option>}
                {currentYear && !CLASS_YEAR_OPTIONS.includes(currentYear) && (
                  <option value={currentYear}>{currentYear}</option>
                )}
                {CLASS_YEAR_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="reload-button-container" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            className="title-button"
            onClick={() => window.open(`${BACKEND_URL}/api/classes/${classId}/indicators/export`, '_blank')}
          >
            Export
          </button>
          {!isTeacher && (
            <button className="upload-cancel-btn" onClick={handleDuplicateClass} disabled={duplicating}>
              {duplicating ? 'Duplicating...' : 'Duplicate'}
            </button>
          )}
          {!isTeacher && (
            confirmDelete ? (
              <div className="delete-confirm">
                <span className="delete-confirm-text">Delete this class?</span>
                <button className="delete-confirm-yes" onClick={handleDeleteClass} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button className="delete-confirm-no" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  Cancel
                </button>
              </div>
            ) : (
              <button className="delete-btn" onClick={() => setConfirmDelete(true)}>
                Delete Class
              </button>
            )
          )}
        </div>
      </div>

      <div className="component-container">
        <div className="class-detail-content">

          <ClassGradingProgress key={classId} classId={classId} onGraded={() => setGradesVersion((v) => v + 1)} />

          {/* Simulation Cases Section */}
          <div className="class-detail-section">
            <div className="class-detail-section-header">
              <h2 className="class-detail-section-title">
                Simulation Cases <span className="class-detail-count">{assignedCases.length}</span>
              </h2>
              {!isTeacher && (
                <button className="title-button" onClick={() => setAssigningCase((v) => !v)}>
                  {assigningCase ? 'Confirm' : '+ Assign Case'}
                </button>
              )}
            </div>

            {assigningCase && !isTeacher ? (
              <AssignCaseTiles
                items={cases.map((c) => ({ id: c.id, label: c.name }))}
                emptyLabel="No simulation cases exist yet."
                assignedIds={assignedCaseIds}
                onToggle={handleToggleCase}
              />
            ) : (
              <div className="class-detail-cases">
                {assignedCases.length === 0 && (
                  <p className="chat-card-hint">No cases assigned yet.</p>
                )}
                {assignedCases.map((c) => (
                  <div
                    key={c.id}
                    className="class-detail-case-card student-row-clickable"
                    draggable={!isTeacher}
                    onDragStart={() => handleCaseDragStart(c.id)}
                    onDragOver={(e) => { e.preventDefault(); handleCaseDragOver(c.id); }}
                    onDrop={handleCaseDrop}
                    onClick={() => navigate(`/admin/cases/${c.id}`)}
                  >
                    <div className="class-detail-case-lead">
                      {!isTeacher && (
                        <span
                          className="drag-handle"
                          onClick={(e) => e.stopPropagation()}
                          title="Drag to reorder"
                        >⠿</span>
                      )}
                      <div className="class-detail-case-main">
                        <div className="class-detail-case-name">
                          <span className="class-detail-case-number">#{caseNumberById.get(c.id)}</span> {c.name}
                        </div>
                      </div>
                    </div>
                    <div className="students-col-actions">
                      <span className="student-view-link">View →</span>
                      {!isTeacher && (
                        <button
                          className="class-remove-btn"
                          onClick={(e) => { e.stopPropagation(); handleRemoveCase(c.id); }}
                          title="Remove from class"
                        >×</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Students Section — roster + per-case completion in one table */}
          <div className="class-detail-section">
            <div className="class-detail-section-header">
              <h2 className="class-detail-section-title">
                Students <span className="class-detail-count">{realAssignedStudentCount} accounts</span>
              </h2>
              {!isTeacher && (
                <button
                  className="title-button"
                  onClick={() => {
                    setAddingStudent((v) => !v);
                    setAddStudentSearchQuery('');
                  }}
                >
                  {addingStudent ? 'Close' : '+ Add Student'}
                </button>
              )}
            </div>

            {addingStudent && !isTeacher && (
              <>
                <input
                  type="text"
                  className="students-search-input assign-panel-search"
                  placeholder="Search students to add..."
                  value={addStudentSearchQuery}
                  onChange={(e) => setAddStudentSearchQuery(e.target.value)}
                  autoFocus
                />
                <AssignPanel
                  items={filteredAvailableStudents.map((s) => ({ id: s.id, label: s.name }))}
                  emptyLabel={availableStudents.length === 0 ? 'All students are already in this class.' : 'No students match your search.'}
                  onPick={handleAddStudent}
                />
              </>
            )}


            {assignedStudents.length > 0 && (
              <div className="students-search-row">
                <input
                  type="text"
                  className="students-search-input"
                  placeholder="Search students in this class..."
                  value={studentSearchQuery}
                  onChange={(e) => setStudentSearchQuery(e.target.value)}
                />
                {completion && assignedCases.length > 0 && (
                  <FilterSelect
                    label="Completion"
                    value={completionFilter}
                    onChange={setCompletionFilter}
                    options={completionFilterOptions}
                  />
                )}
                {isFilteringStudents && (
                  <>
                    <span className="students-search-count">
                      {/* Real students only, matching the header's account count */}
                      {filteredRealAssignedStudents.length} of {realAssignedStudentCount}
                    </span>
                    <button
                      type="button"
                      className="students-clear-filters"
                      onClick={() => { setStudentSearchQuery(''); setCompletionFilter(''); }}
                    >
                      Clear filters
                    </button>
                  </>
                )}
                {loadingCompletion && <span className="students-search-count">Loading case completion...</span>}
                <button
                  type="button"
                  className={`students-copy-button${copyStatus === 'copied' ? ' students-copy-button-done' : ''}`}
                  onClick={handleCopyEmails}
                  disabled={visibleEmails.length === 0}
                  title="Copy the emails of the students shown below, comma-separated (excludes demo accounts)"
                >
                  {copyStatus === 'copied' ? `✓ Copied ${emailCountLabel}`
                    : copyStatus === 'failed' ? 'Copy failed — check clipboard permissions'
                    : `Copy ${emailCountLabel}`}
                </button>
              </div>
            )}

            <div className="completion-table-wrap">
              <div className="completion-table">
                {completionHeaderRow}
                {assignedStudents.length === 0 && (
                  <div className="completion-row"><span className="chat-card-hint">No students yet.</span></div>
                )}
                {assignedStudents.length > 0 && filteredRealAssignedStudents.length === 0 && (
                  <div className="completion-row"><span className="chat-card-hint">
                    {studentSearchQuery ? `No students match "${studentSearchQuery}".` : 'No students match this filter.'}
                  </span></div>
                )}
                {filteredRealAssignedStudents.map((s) => renderCompletionRow(s, filteredRealAssignedStudents))}
              </div>
            </div>

            {showDemoStudentsSection && (
              <>
                <h2 className="cases-section-heading">
                  Demo Accounts <span className="cases-section-hint">{demoAssignedStudentCount} accounts</span>
                </h2>
                <div className="completion-table-wrap">
                  <div className="completion-table">
                    {completionHeaderRow}
                    {filteredDemoAssignedStudents.length === 0 && (
                      <div className="completion-row"><span className="chat-card-hint">
                        {studentSearchQuery ? `No demo accounts match "${studentSearchQuery}".` : 'No demo accounts match this filter.'}
                      </span></div>
                    )}
                    {filteredDemoAssignedStudents.map((s) => renderCompletionRow(s, filteredDemoAssignedStudents))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Class competency development over time */}
          <div className="case-manage-section">
            <button
              type="button"
              className="case-manage-collapsible-header"
              aria-expanded={progressionOpen}
              onClick={() => setProgressionOpen((v) => !v)}
            >
              <span className="case-manage-collapsible-title">Class Competency Development Over Time</span>
              <span className="case-manage-collapsible-summary">
                {progressionCodes.length} sub-competenc{progressionCodes.length === 1 ? 'y' : 'ies'} tracked
              </span>
              <span className="case-manage-collapsible-chevron">{progressionOpen ? '▾' : '▸'}</span>
            </button>
            <div className="case-manage-collapsible-body" hidden={!progressionOpen}>
              <div className="progression-group-row">
                <FilterSelect
                  label="Show"
                  value={trendsGroup}
                  onChange={setTrendsGroup}
                  defaultValue="student"
                  options={[
                    { value: 'student', label: 'Students' },
                    { value: 'demo', label: 'Demo Accounts' },
                  ]}
                />
                {!trendsLoading && !trendsError && typeof trends?.totalStudents === 'number' && (
                  <span className="chat-card-hint">
                    {trends.totalStudents} {trendsGroup === 'demo' ? 'demo account' : 'student'}{trends.totalStudents === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <p className="config-intro">
                Share of {classItem.name} {trendsGroup === 'demo' ? 'demo accounts' : 'students'} graded Developed, Developing, or Not Yet Evidenced on each AACN sub-competency, tracked weekly across the whole class.
              </p>

              {trendsLoading ? (
                <p className="chat-card-hint">Loading progression data...</p>
              ) : trendsError ? (
                <p className="chat-card-hint">Could not load progression data. Please try again.</p>
              ) : progressionCodes.length === 0 ? (
                <div className="progression-grid">
                  <div className="progression-card">
                    <div className="progression-card-header">
                      <span className="progression-card-title">No sub-competencies assigned</span>
                    </div>
                    <span className="progression-card-domain">Assign a case with AACN sub-competencies to start tracking</span>
                    <ProgressionChart series={[]} labels={[]} emptyMessage="No cases assigned yet" />
                  </div>
                </div>
              ) : (
                <div className="progression-grid">
                  {progressionCodes.map((code) => {
                    const sc = getSubCompetencyByCode(code);
                    const series = (trends.timeline || []).map((period) => levelPercentages(period.bySubCompetency?.[code]));
                    return (
                      <div key={code} className="progression-card">
                        <div className="progression-card-header">
                          <span className="indicator-row-code">{code}</span>
                          <span className="progression-card-title">
                            {aacnLoading ? 'Loading…' : (sc?.subCompetency || 'Unknown sub-competency')}
                          </span>
                        </div>
                        {sc?.domainName && <span className="progression-card-domain">{sc.domainName}</span>}
                        <ProgressionChart series={series} labels={progressionLabels} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ClassDetail;
